import { BRAND_DARK_MODE } from '~/components/brand';

/**
 * Local UI tokens for the AskRichard (DataChatbot) surface.
 *
 * These are scoped to this feature rather than the shared MUI theme because the
 * theme owns semantic roles (primary/success/warning/error) that must not double
 * as categorical chart colours. The chart series palette itself lives with the
 * charts that use it (LeLens/lensChart.ts, built on the five brand tokens): this
 * file previously carried its own fixed-slot palette (Ocean Teal paired with
 * Burnt Orange, an off-palette colour) but nothing consumed it, since every
 * chart on this surface already renders through LensEChart. Removed rather
 * than reskinned, to avoid leaving a dead, off-brand palette for the next
 * caller to copy.
 */

/** Monospace/data font stack. No theme token exists for this; keep it local. */
export const MONO_FONT_STACK = 'ui-monospace, "SF Mono", "Cascadia Mono", "Roboto Mono", Menlo, monospace';

/**
 * Serif accent voice (Suisse Works), loaded app-wide. Referenced by family string, never imported
 * as a module. Lives here so the chat surface and its launchpad share one definition.
 */
export const SERIF_ACCENT_FONT = '"Lens Suisse Works", Georgia, serif';

/** Elevated artifact surface in dark mode (cards inside answers): the brand's card-on-teal-field pair. */
export const ELEVATED_SURFACE_DARK = BRAND_DARK_MODE.card;

/** The rule between that elevated card and the dark canvas behind it. Never black. */
export const ELEVATED_SURFACE_DARK_RULE = BRAND_DARK_MODE.cardRule;

/**
 * A legend only earns its space when more than one series is plotted; a single-series
 * chart is named by its title, so the legend would just restate it.
 */
export function shouldShowLegend(seriesCount: number): boolean {
  return seriesCount >= 2;
}

/**
 * A chart block is empty when it carries no plottable rows. The renderer shows a neutral
 * "No data for this view." state at chart height instead of an axis frame with nothing in
 * it (or, historically, a raw JSON dump of the payload). A non-array `data` (a malformed
 * payload) is treated as empty for the same reason: the user gets the honest empty state,
 * not developer debug output.
 */
export function isEmptyChart(data: unknown): boolean {
  return !Array.isArray(data) || data.length === 0;
}

/**
 * Horizontal gridline stroke: the theme divider colour dropped to a low opacity so lines
 * read as a faint reference grid rather than structure. Branches on surface so the grid
 * stays barely-there on both the white light surface and the dark (#181d23) surface.
 */
export function getChartGridStroke(mode: 'light' | 'dark'): string {
  return mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';
}

/* -------------------------------------------------------------------------- */
/* KPI stat-tile helpers                                                       */
/* -------------------------------------------------------------------------- */

/** At most four headline tiles read as a scannable row; extras fold rather than crowd the line. */
export const MAX_KPI_TILES = 4;

/** Semantic polarity of a KPI delta. Carried in the payload, never inferred from the numeric sign. */
export type KpiDeltaSentiment = 'positive' | 'negative' | 'neutral';

/**
 * Normalises a delta sentiment hint to one of the three semantic buckets. Anything absent or
 * unrecognised falls back to neutral: we never guess "good" or "bad" from the sign of the number,
 * because an increase can be either (revenue up is good, churn up is bad).
 */
export function normalizeKpiSentiment(raw: unknown): KpiDeltaSentiment {
  const value = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (value === 'positive') {
    return 'positive';
  }
  if (value === 'negative') {
    return 'negative';
  }
  return 'neutral';
}

/**
 * The theme palette path a delta is coloured with, chosen by sentiment (not by numeric sign).
 * Returned as a token path so MUI resolves it correctly in both light and dark themes.
 */
export function kpiDeltaColorRole(sentiment: KpiDeltaSentiment): 'success.main' | 'error.main' | 'text.secondary' {
  switch (sentiment) {
    case 'positive':
      return 'success.main';
    case 'negative':
      return 'error.main';
    default:
      return 'text.secondary';
  }
}

/** Direction a delta arrow points. Conveys movement; distinct from sentiment, which conveys good/bad. */
export type KpiDeltaDirection = 'up' | 'down' | 'flat';

/**
 * Arrow glyph plus an accessible word per direction. The glyph carries direction on its own, so the
 * delta is never distinguished by colour alone (colour still carries sentiment).
 */
export const KPI_DELTA_DIRECTION: Record<KpiDeltaDirection, { glyph: string; label: string }> = {
  up: { glyph: '↑', label: 'Up' },
  down: { glyph: '↓', label: 'Down' },
  flat: { glyph: '→', label: 'No change' },
};

/**
 * Direction for the delta arrow: an explicit up/down/flat hint wins; otherwise the numeric sign of
 * the delta decides (a preformatted string like "+12%" or "-3.5%" has its leading number parsed).
 * Zero, missing, or unparseable deltas read as flat.
 */
export function deriveKpiDeltaDirection(delta: unknown, explicit?: unknown): KpiDeltaDirection {
  const hint = typeof explicit === 'string' ? explicit.trim().toLowerCase() : '';
  if (hint === 'up' || hint === 'down' || hint === 'flat') {
    return hint;
  }
  const numeric =
    typeof delta === 'number'
      ? delta
      : Number(
          (String(delta ?? '')
            .replace(/,/g, '')
            .match(/[+-]?\d*\.?\d+/) ?? [])[0],
        );
  if (!Number.isFinite(numeric) || numeric === 0) {
    return 'flat';
  }
  return numeric > 0 ? 'up' : 'down';
}

/** Block kinds that render as KPI stat tiles, compared after case-folding and stripping space/_/-. */
const KPI_BLOCK_KINDS = new Set(['kpi', 'kpis', 'stat', 'stats', 'metric', 'metrics', 'kpitiles']);

/** Case-fold a kind string and strip word separators so 'KPI_Tiles' and 'kpi-tiles' both match 'kpitiles'. */
export function normalizeBlockKind(raw: unknown): string {
  return typeof raw === 'string' ? raw.toLowerCase().replace(/[\s_-]+/g, '') : '';
}

/** Whether a raw type/kind string names a KPI stat-tile block. */
export function isKpiBlockKind(raw: unknown): boolean {
  return KPI_BLOCK_KINDS.has(normalizeBlockKind(raw));
}

/* -------------------------------------------------------------------------- */
/* Steering-as-hero helpers                                                    */
/* -------------------------------------------------------------------------- */

/** localStorage key gating the one-time steer coach mark. Namespaced so it never collides. */
export const STEER_COACH_MARK_STORAGE_KEY = 'askRichard.steerCoachMark.seen';

/**
 * How many of a message's steers have been acted on, so the render can show "askRichard adjusted
 * course" only under a steer askRichard has responded to. While the answer is still streaming we
 * trust the live count of steers that a later progress event acknowledged; once the run is no
 * longer streaming every steer it received was folded into the finished answer, so all are acked.
 * The live count is clamped into range so a stale tracker can never claim more acks than steers.
 */
export function resolveSteerAckCount(params: {
  isLiveStreaming: boolean;
  liveAckCount: number;
  totalSteers: number;
}): number {
  const { isLiveStreaming, liveAckCount, totalSteers } = params;
  if (!isLiveStreaming) {
    return totalSteers;
  }
  if (!Number.isFinite(liveAckCount) || liveAckCount <= 0) {
    return 0;
  }
  return Math.min(Math.floor(liveAckCount), totalSteers);
}

/**
 * Whether the coach mark has already been shown. Reads localStorage defensively: a throwing or
 * absent store (private mode, SSR) is treated as "not seen" so the feature still works, it just
 * loses its one-time guard rather than crashing.
 */
export function hasSeenSteerCoachMark(): boolean {
  try {
    return window.localStorage.getItem(STEER_COACH_MARK_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Persist that the coach mark has been shown, so it never reappears. Swallows storage errors. */
export function markSteerCoachMarkSeen(): void {
  try {
    window.localStorage.setItem(STEER_COACH_MARK_STORAGE_KEY, 'true');
  } catch {
    // Ignore: a full or unavailable store just means the mark may show again next session.
  }
}

/* -------------------------------------------------------------------------- */
/* Motion system                                                               */
/* -------------------------------------------------------------------------- */
/*
 * A small, deliberately restrained motion layer: CSS/Emotion keyframes only, no animation library.
 * These tokens are the single source of truth for easing and duration so every animated surface on
 * the chat page reads as one system. Every motion below has a `prefers-reduced-motion: reduce`
 * fallback that drops to opacity-only or removes the transform entirely.
 */

/** Entrances (things arriving): a soft expo-out so they settle rather than snap. */
export const EASE_ENTRANCE = 'cubic-bezier(0.16, 1, 0.3, 1)';
/** State changes (borders, presses, rotations): a gentle standard curve. */
export const EASE_STATE = 'cubic-bezier(0.2, 0, 0, 1)';
/** Exits (things leaving): a quick ease-in so they get out of the way. */
export const EASE_EXIT = 'ease-in';

/** Newest-message entrance duration (ms). */
export const DUR_ENTRANCE = 280;
/** State-change / chevron-rotation duration (ms). */
export const DUR_STATE = 200;
/** Icon-press micro-interaction duration (ms). */
export const DUR_PRESS = 120;
/** Exit duration (ms). */
export const DUR_EXIT = 120;

/** How far the newest message rises as it fades in (px). */
export const MESSAGE_ENTRANCE_RISE_PX = 8;

/**
 * Entrance sx for a message row. Only the newest, just-appended message should pass `true` (see
 * resolveMessageEntrance): it fades in and rises 8px over 280ms. Reduced motion keeps the fade but
 * drops the transform. Returns undefined when the message must not animate, so spreading it into an
 * sx object (or passing it straight through) is a no-op for every other row.
 */
export function messageEntranceSx(isEntrance: boolean) {
  if (!isEntrance) {
    return undefined;
  }
  return {
    animation: `askRichardMessageEntrance ${DUR_ENTRANCE}ms ${EASE_ENTRANCE} both`,
    '@keyframes askRichardMessageEntrance': {
      from: { opacity: 0, transform: `translateY(${MESSAGE_ENTRANCE_RISE_PX}px)` },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: `askRichardMessageEntranceReduced ${DUR_ENTRANCE}ms ${EASE_ENTRANCE} both`,
      '@keyframes askRichardMessageEntranceReduced': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
    },
  } as const;
}

/**
 * Icon-button press micro-interaction: the glyph dips to 92% while held, then springs back. Shared
 * across the answer action bar so presses feel physical without any single control shouting. Reduced
 * motion removes the transform entirely.
 */
export const ICON_PRESS_SX = {
  transition: `transform ${DUR_PRESS}ms ${EASE_STATE}`,
  '&:active': { transform: 'scale(0.92)' },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&:active': { transform: 'none' },
  },
} as const;

/** A proper (strictly shorter) prefix: every id in `a` matches the same position in `b`, and `b` is longer. */
function isProperPrefix(a: readonly string[], b: readonly string[]): boolean {
  if (a.length >= b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/** What the chat page remembers between renders to decide whether the newest message just arrived. */
export interface MessageEntranceTracker {
  conversationKey: string | null;
  messageIds: readonly string[];
}

/**
 * Decides which message (if any) should play the entrance animation, and returns the tracker state
 * to remember for next time. The rule keeps the animation to genuinely new arrivals only:
 *
 *  - First observation (prev === null): seed silently. A page load that hydrates existing history
 *    never animates.
 *  - Conversation switch (key changed): a batch load, never an entrance.
 *  - Same conversation, prior id list is a proper prefix of the current one: a live append (a sent
 *    question and its answer), so the newest id animates.
 *  - Otherwise (streaming into the same message, a plain re-render): keep whatever is already
 *    animating so the property stays stable and the running animation is never cut short.
 *
 * Consequence by design: the very first message of a brand-new conversation (where the key flips
 * from null to a fresh draft in the same tick) does not animate. That errs toward the hard
 * constraint - never animate a load - rather than risk animating switched-in history.
 */
export function resolveMessageEntrance(params: {
  messageIds: readonly string[];
  conversationKey: string | null;
  prev: MessageEntranceTracker | null;
  currentEntranceId: string | null;
}): { entranceId: string | null; next: MessageEntranceTracker } {
  const { messageIds, conversationKey, prev, currentEntranceId } = params;
  const newestId = messageIds.length > 0 ? messageIds[messageIds.length - 1] : null;
  const next: MessageEntranceTracker = { conversationKey, messageIds };

  if (prev === null || prev.conversationKey !== conversationKey) {
    return { entranceId: null, next };
  }
  if (newestId !== null && isProperPrefix(prev.messageIds, messageIds)) {
    return { entranceId: newestId, next };
  }
  return { entranceId: currentEntranceId, next };
}
